// Main data store (LEGACY NAME — no Firestore at runtime; it talks to the
// Supabase Edge Functions API via databaseService). Holds the reactive state
// for all standard collections (expenses, categories, accounts, income,
// savings, settlements, investments, preferences), wires up the
// initial-fetch + Realtime-refetch subscriptions, and applies optimistic
// updates for mutations. Data flow: view → this store → databaseService →
// apiClient → /api/* (see docs/ARCHITECTURE.md).

import { computed, reactive, ref } from "vue"
import i18n from "../i18n"
import { isQueuedOfflineError, type Unsubscribe } from "../services/apiClient"
import type { AuthUser } from "../services/authService"
import { type DocUpdate, databaseService } from "../services/databaseService"
import type {
  FirestoreAccount,
  FirestoreCategory,
  FirestoreDebt,
  FirestoreDocument,
  FirestoreExpense,
  FirestoreExpenseComment,
  FirestoreIncome,
  FirestoreInvestment,
  FirestoreSavingsGoal,
  FirestoreSavingsTransaction,
  FirestoreSettlement,
  FirestoreUserPreferences,
} from "../types/firestore"
import { countsTowardSpending } from "../utils/budgetVsActual"
import { logger } from "../utils/logger"
import { calculateMonthlyIncome } from "../utils/monthlyIncome"
import { sanitizeText } from "../utils/sanitize"
import { personalCurrency } from "./currencyPreference"
import { checkBudgetAlert, checkGoalMilestone, refreshAllNotifications } from "./notificationStore"

// Local state that mirrors Firestore
interface FirestoreState {
  expenses: FirestoreExpense[]
  categories: FirestoreCategory[]
  accounts: FirestoreAccount[]
  income: FirestoreIncome[]
  savingsGoals: FirestoreSavingsGoal[]
  investments: FirestoreInvestment[]
  debts: FirestoreDebt[]
  savingsTransactions: FirestoreSavingsTransaction[]
  settlements: FirestoreSettlement[]
  expenseComments: FirestoreExpenseComment[]
  preferences: FirestoreUserPreferences | null
  isLoading: boolean
  isInitialized: boolean
  error: string | null
}

const state = reactive<FirestoreState>({
  expenses: [],
  categories: [],
  accounts: [],
  income: [],
  savingsGoals: [],
  investments: [],
  debts: [],
  savingsTransactions: [],
  settlements: [],
  expenseComments: [],
  preferences: null,
  isLoading: false,
  isInitialized: false,
  error: null,
})

// Firestore subscriptions
const subscriptions = ref<{
  expenses?: Unsubscribe
  categories?: Unsubscribe
  accounts?: Unsubscribe
  income?: Unsubscribe
  savingsGoals?: Unsubscribe
  investments?: Unsubscribe
  debts?: Unsubscribe
  savingsTransactions?: Unsubscribe
  settlements?: Unsubscribe
  expenseComments?: Unsubscribe
}>({})

// Default categories that will be created for new users. Names are localized
// at seed time (category names are user data — stored, not translated on
// display), so a Norwegian first run gets Norwegian starter categories.
const DEFAULT_CATEGORY_SEEDS = [
  { key: "categories.defaults.food", color: "#ef4444", budget: 0 },
  { key: "categories.defaults.transportation", color: "#f97316", budget: 0 },
  { key: "categories.defaults.shopping", color: "#eab308", budget: 0 },
  { key: "categories.defaults.entertainment", color: "#22c55e", budget: 0 },
  { key: "categories.defaults.bills", color: "#3b82f6", budget: 0 },
  { key: "categories.defaults.healthcare", color: "#a855f7", budget: 0 },
  { key: "categories.defaults.other", color: "#6b7280", budget: 0 },
]

function defaultCategories(): { name: string; color: string; budget: number }[] {
  return DEFAULT_CATEGORY_SEEDS.map(({ key, color, budget }) => ({
    name: i18n.global.t(key),
    color,
    budget,
  }))
}

// The names a brand-new budget is seeded with (in the current locale). Exposed
// so the household setup wizard can offer per-category split rules before the
// household's categories exist (they're created on first entry); the rules
// match by name once seeded.
export function getDefaultCategoryNames(): string[] {
  return defaultCategories().map((c) => c.name)
}

// Computed values
export const isFirestoreInitialized = computed(() => state.isInitialized)
export const isFirestoreLoading = computed(() => state.isLoading)
export const firestoreError = computed(() => state.error)

// Lookup maps: rebuilt only when the underlying array reference changes (which
// is on every Firestore snapshot, not on every individual mutation), so views
// that resolve names/colors many times per render avoid an O(n) find() per
// call. The same map covers id-keyed and name-keyed lookups since historical
// data sometimes stores the category/account by name rather than id.
export const categoriesById = computed(() => {
  const map = new Map<string, FirestoreCategory>()
  for (const c of state.categories) {
    map.set(c.id, c)
    map.set(c.name, c)
  }
  return map
})

export const accountsById = computed(() => {
  const map = new Map<string, FirestoreAccount>()
  for (const a of state.accounts) {
    map.set(a.id, a)
    map.set(a.name, a)
  }
  return map
})

// "YYYY-MM" key for the current calendar month, recomputed each access. Cheap,
// no Date allocation in callers — used to gate budget-alert recalculation.
function currentMonthKey(): string {
  const now = new Date()
  const m = now.getMonth() + 1
  return `${now.getFullYear()}-${m < 10 ? `0${m}` : m}`
}

// Running total of this-month expense per category, keyed by both id and name
// (mirroring how expenses store the category reference). Rebuilt when the
// expenses array reference changes — i.e., once per Firestore snapshot, not
// once per addExpense() call. Recurring templates are excluded: they are the
// budget plan, not spend — counting them alongside their generated
// occurrences would double every recurring bill.
export const currentMonthSpendByCategory = computed(() => {
  const map = new Map<string, number>()
  const month = currentMonthKey()
  for (const e of state.expenses) {
    if (e.type !== "expense" || e.isRecurring) continue
    if (!e.date.startsWith(month)) continue
    const key = e.category
    map.set(key, (map.get(key) || 0) + e.amount)
  }
  return map
})

// Monthly-normalized income. Delegates to the shared conversion util so every
// surface (dashboard, insights, AI prompts) agrees on the factors — a local
// reducer here previously drifted (it counted bi-weekly/quarterly/one-time
// income at face value once per month). Paused sources don't earn anything,
// so they're excluded.
export const totalIncome = computed(() => {
  return calculateMonthlyIncome(state.income.filter((income) => income.isActive !== false))
})

// This calendar month's expense total. budgetSummary pairs it with the
// monthly-normalized income above, so remaining/ratios stay meaningful instead
// of comparing all-time spend against one month of income. Recurring
// templates don't count — they're plans; their generated occurrences carry
// the actual spend.
export const totalExpenses = computed(() => {
  const month = currentMonthKey()
  return state.expenses
    .filter((e) => e.type === "expense" && countsTowardSpending(e) && e.date.startsWith(month))
    .reduce((sum, e) => sum + e.amount, 0)
})

// Net savings-goal contributions made this calendar month (deposits minus
// withdrawals). Interest/dividends grow inside the goal without leaving the
// month's budget, so they don't reduce `remaining`. The goals' all-time
// balance is a stock, not a monthly flow — subtracting it from monthly income
// made `remaining` drift ever more negative.
export const totalSavings = computed(() => {
  const month = currentMonthKey()
  return state.savingsTransactions.reduce((sum, t) => {
    if (!t.date.startsWith(month)) return sum
    if (t.type === "deposit") return sum + t.amount
    if (t.type === "withdrawal") return sum - t.amount
    return sum
  }, 0)
})

export const accountsTotal = computed(() => {
  return state.accounts.reduce((sum, a) => sum + a.balance, 0)
})

// What is still owed on active loans — the liability side of accountsTotal.
// Settled loans stay on the books as history and are excluded here.
export const debtsTotal = computed(() =>
  state.debts.reduce((sum, d) => (d.isActive === false ? sum : sum + (Number(d.balance) || 0)), 0),
)

// Helper function for testing
export function resetFirestoreState() {
  initInFlight = null
  initInFlightUid = null
  state.expenses = []
  state.categories = []
  state.accounts = []
  state.income = []
  state.savingsGoals = []
  state.investments = []
  state.debts = []
  state.savingsTransactions = []
  state.settlements = []
  state.expenseComments = []
  state.preferences = null
  state.isLoading = false
  state.isInitialized = false
  state.error = null
}

// All figures are current-month: income normalized to a monthly amount,
// expenses and savings contributions scoped to this calendar month.
// accountsTotal is the only stock (current balances).
export const budgetSummary = computed(() => ({
  totalIncome: totalIncome.value,
  totalExpenses: totalExpenses.value,
  totalSavings: totalSavings.value,
  remaining: totalIncome.value - totalExpenses.value - totalSavings.value,
  accountsTotal: accountsTotal.value,
}))

// In-flight initialization guard: the `isInitialized` check alone is
// non-atomic (it only flips at the end of the async init), so two same-tick
// calls would both proceed and duplicate every Realtime subscription. While an
// init for the same user is running, callers get the same promise back.
// `cleanupFirestore()` (called on logout / context switch) clears it so a new
// context always re-initializes.
let initInFlight: Promise<void> | null = null
let initInFlightUid: string | null = null

// Init generation: bumped by every new init and by cleanupFirestore(). An
// in-flight init compares its own generation after every await — if it no
// longer matches, a context switch superseded it and it must stop: seeding
// would hit the WRONG context (createCategory reads the database service's
// household id at call time), and storing its channels would leak the 8
// Realtime subscriptions the new init is about to replace.
let initGeneration = 0

/**
 * Initialize Firestore store for a user
 */
export function initializeFirestore(user: AuthUser | null): Promise<void> {
  if (!user) {
    return Promise.resolve()
  }

  if (state.isInitialized) {
    return Promise.resolve()
  }

  if (initInFlight && initInFlightUid === user.uid) {
    return initInFlight
  }

  const promise = doInitializeFirestore(user).finally(() => {
    // Only clear if a cleanup/newer init hasn't replaced us in the meantime.
    if (initInFlight === promise) {
      initInFlight = null
      initInFlightUid = null
    }
  })
  initInFlight = promise
  initInFlightUid = user.uid
  return promise
}

async function doInitializeFirestore(user: AuthUser): Promise<void> {
  const generation = ++initGeneration
  const isStale = () => generation !== initGeneration

  state.isLoading = true
  state.error = null

  try {
    // Set the user in database service
    databaseService.setUser(user)
    activeUserId = user.uid

    // Check if this is a new user by looking for existing data. This is a
    // one-shot read: if it fails (API/network down) we must NOT hang here —
    // treat it as "not a new user" (skip seeding) and let the live
    // subscriptions below recover the data when the API comes back. A failing
    // read used to leave a subscribe-once promise pending forever, which kept
    // isInitialized false and stuck the whole UI on skeletons.
    let existingCategories: FirestoreCategory[] | null = null
    try {
      existingCategories = await databaseService.getCategories()
    } catch (error) {
      logger.error("Failed to load categories during init; skipping new-user seeding", error)
    }
    if (isStale()) return

    // If no categories exist, create default ones (new user). Only seed when the
    // read actually succeeded and came back empty — never when it failed.
    if (existingCategories !== null && existingCategories.length === 0) {
      for (const category of defaultCategories()) {
        // Re-check per write: a context switch mid-seed would send the
        // remaining categories to the new context.
        if (isStale()) return
        await databaseService.createCategory(category)
      }
      if (isStale()) return

      // Also check for local data to migrate
      try {
        const localData = localStorage.getItem("budget-app-data")
        if (localData) {
          const parsed = JSON.parse(localData)
          await databaseService.migrateLocalData(parsed)

          // Clear local storage after successful migration
          localStorage.removeItem("budget-app-data")
        }
      } catch (error) {
        logger.error("Failed to migrate local data", error)
      }
      if (isStale()) return
    }

    // Set up real-time subscriptions. Built into a local object and committed
    // atomically below, so a superseded init can tear its channels down
    // instead of overwriting (and leaking) the ones the current init owns.
    const subs: typeof subscriptions.value = {}

    subs.expenses = databaseService.subscribeToExpenses((expenses) => {
      state.expenses = expenses
    })

    subs.categories = databaseService.subscribeToCategories((categories) => {
      state.categories = categories
    })

    subs.accounts = databaseService.subscribeToAccounts((accounts) => {
      state.accounts = accounts
    })

    subs.income = databaseService.subscribeToIncome((income) => {
      state.income = income
    })

    subs.savingsGoals = databaseService.subscribeToSavingsGoals((goals) => {
      state.savingsGoals = goals
    })

    subs.investments = databaseService.subscribeToInvestments((investments) => {
      state.investments = investments
    })

    subs.debts = databaseService.subscribeToDebts((debts) => {
      state.debts = debts
    })

    subs.savingsTransactions = databaseService.subscribeToSavingsTransactions((transactions) => {
      state.savingsTransactions = transactions
    })

    subs.settlements = databaseService.subscribeToSettlements((settlements) => {
      state.settlements = settlements
    })

    subs.expenseComments = databaseService.subscribeToExpenseComments((comments) => {
      state.expenseComments = comments
    })

    if (isStale()) {
      Object.values(subs).forEach((unsubscribe) => {
        if (unsubscribe) unsubscribe()
      })
      return
    }
    subscriptions.value = subs

    // Load user preferences. Non-fatal on failure (e.g. starting offline) —
    // the cached list subscriptions above still let the app render.
    const preferences = await databaseService.getUserPreferences().catch((error) => {
      logger.warn("Failed to load user preferences", { error })
      return null
    })
    if (isStale()) return
    state.preferences = preferences
    personalCurrency.value = preferences?.currency ?? null

    state.isInitialized = true
  } catch (error) {
    if (!isStale()) state.error = `Failed to initialize database: ${error}`
  } finally {
    // A stale init must not clear the loading flag the superseding init set.
    if (!isStale()) state.isLoading = false
  }
}

/**
 * Clean up Firestore subscriptions
 */
export function cleanupFirestore() {
  // Forget any in-flight initialization: the next initializeFirestore() call
  // (e.g. after a context switch) must start fresh instead of reusing a
  // promise that was set up for the previous context.
  initInFlight = null
  initInFlightUid = null
  activeUserId = null

  // Invalidate any in-flight init: it must not seed data into the wrong
  // context or store subscriptions for a context that is no longer active.
  initGeneration++

  // Unsubscribe from all real-time listeners
  Object.values(subscriptions.value).forEach((unsubscribe) => {
    if (unsubscribe) unsubscribe()
  })

  // Reset state
  state.expenses = []
  state.categories = []
  state.accounts = []
  state.income = []
  state.savingsGoals = []
  state.investments = []
  state.debts = []
  state.savingsTransactions = []
  state.settlements = []
  state.expenseComments = []
  state.preferences = null
  state.isInitialized = false
  state.isLoading = false
  state.error = null

  // The currency preference is user-scoped module state, not part of `state` —
  // without this reset the next account on a shared device renders amounts in
  // the previous user's currency until their own preferences load.
  personalCurrency.value = null

  subscriptions.value = {}
}

// ---------------------------------------------------------------------------
// Optimistic mutations
//
// Writes apply to local state immediately so the UI never waits for the API
// round-trip (2-3s on slow mobile networks). The Realtime-triggered refetch
// replaces each list wholesale with authoritative server data, which also
// reconciles temp ids. Rollback policy on failure:
//   - request queued in the offline outbox → keep the optimistic state; the
//     outbox replays it when connectivity returns
//   - HTTP error (validation, auth, server) → undo and rethrow
//
// Known edge: editing or deleting a row that was itself created offline (its
// id is still a temp id) queues a request the server will 404 — the replay
// drops it and the next refetch settles the truth.
// ---------------------------------------------------------------------------

// Set at init; used to stamp userId on optimistic rows before the server
// assigns the real envelope.
let activeUserId: string | null = null

let tempIdSeq = 0
const makeTempId = () => `optimistic-${Date.now()}-${++tempIdSeq}`

// Sort comparators mirroring each collection's server-side order, so an
// optimistic row lands where the refetched data will put it.
const byDateDesc = (a: { date: string }, b: { date: string }) => b.date.localeCompare(a.date)
const byName = (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name)

async function optimisticCreate<T extends FirestoreDocument>(
  read: () => T[],
  write: (rows: T[]) => void,
  doc: Omit<T, "id" | "userId" | "createdAt" | "updatedAt">,
  create: () => Promise<string>,
  compare?: (a: T, b: T) => number,
): Promise<string> {
  const now = new Date().toISOString()
  const temp = { ...doc, id: makeTempId(), userId: activeUserId ?? "", createdAt: now, updatedAt: now } as T
  const next = [temp, ...read()]
  if (compare) next.sort(compare)
  write(next)
  try {
    const id = await create()
    write(read().map((row) => (row.id === temp.id ? { ...row, id } : row)))
    return id
  } catch (error) {
    if (isQueuedOfflineError(error)) return temp.id
    write(read().filter((row) => row.id !== temp.id))
    throw error
  }
}

async function optimisticUpdate<T extends FirestoreDocument>(
  read: () => T[],
  write: (rows: T[]) => void,
  id: string,
  // null means "clear this field" (see DocUpdate in databaseService). The
  // server omits null columns on read, so locally a null field is equivalent
  // to an absent one — the cast below is safe for every falsy-checked read.
  updates: { [K in keyof T]?: T[K] | null },
  update: () => Promise<void>,
): Promise<void> {
  const previous = read().find((row) => row.id === id)
  if (previous) {
    write(read().map((row) => (row.id === id ? ({ ...row, ...updates } as T) : row)))
  }
  try {
    await update()
  } catch (error) {
    if (isQueuedOfflineError(error)) return
    if (previous) write(read().map((row) => (row.id === id ? previous : row)))
    throw error
  }
}

async function optimisticDelete<T extends FirestoreDocument>(
  read: () => T[],
  write: (rows: T[]) => void,
  id: string,
  remove: () => Promise<void>,
): Promise<void> {
  const removed = read().find((row) => row.id === id)
  if (removed) write(read().filter((row) => row.id !== id))
  try {
    await remove()
  } catch (error) {
    if (isQueuedOfflineError(error)) return
    // Re-append on failure; the next refetch restores canonical order.
    if (removed) write([...read(), removed])
    throw error
  }
}

// Duplicate-detection window: a transaction with identical amount + category +
// account + date + description added within this window is treated as a possible
// duplicate. 60 seconds catches the "double-tap submit" case without blocking
// genuine repeat purchases later in the day.
const DUPLICATE_WINDOW_MS = 60_000

/** Returns true if an effectively identical expense was added in the last minute. */
export function findDuplicateExpense(
  expense: Pick<FirestoreExpense, "amount" | "description" | "category" | "account" | "date" | "type">,
): FirestoreExpense | undefined {
  const now = Date.now()
  return state.expenses.find((existing) => {
    if (existing.type !== expense.type) return false
    if (existing.amount !== expense.amount) return false
    if (existing.category !== expense.category) return false
    if (existing.account !== expense.account) return false
    if (existing.date !== expense.date) return false
    if ((existing.description || "").trim() !== (expense.description || "").trim()) return false
    // createdAt is either a Firestore FieldValue sentinel (unknown shape) or a
    // serialized timestamp once the doc round-trips. Be defensive — only the
    // numeric form is useful for the duplicate-window check.
    const ts = existing.createdAt as { seconds?: number } | string | number | undefined
    let createdAt = 0
    if (typeof ts === "number") createdAt = ts
    else if (typeof ts === "string") createdAt = new Date(ts).getTime()
    else if (ts && typeof ts === "object" && typeof ts.seconds === "number") createdAt = ts.seconds * 1000
    return createdAt > 0 && now - createdAt < DUPLICATE_WINDOW_MS
  })
}

// EXPENSE OPERATIONS
export async function addExpense(
  expense: Omit<FirestoreExpense, "id" | "userId" | "createdAt" | "updatedAt">,
  options: { skipDuplicateCheck?: boolean } = {},
) {
  // Strip HTML from user-provided text fields. Descriptions are user input and
  // get rendered as plain text — but if they ever flow through a v-html or CSV
  // export, this prevents script injection at the storage boundary.
  const cleaned: typeof expense = {
    ...expense,
    description: sanitizeText(expense.description || ""),
    category: sanitizeText(expense.category || ""),
    account: sanitizeText(expense.account || ""),
  }

  if (!options.skipDuplicateCheck) {
    const dup = findDuplicateExpense(cleaned)
    if (dup) {
      const err = new Error("DUPLICATE_TRANSACTION") as Error & { duplicate: FirestoreExpense }
      err.duplicate = dup
      throw err
    }
  }

  const id = await optimisticCreate<FirestoreExpense>(
    () => state.expenses,
    (rows) => {
      state.expenses = rows
    },
    cleaned,
    () => databaseService.createExpense(cleaned),
    byDateDesc,
  )

  // Check for budget alerts after adding an expense. The pre-aggregated
  // current-month spend map gives an O(1) lookup instead of re-filtering
  // every expense per add; the optimistic row is already in state, so the
  // map total is the post-add total. Recurring templates are plans, not
  // spend, so creating one never moves the month's total.
  if (
    cleaned.type === "expense" &&
    !cleaned.isRecurring &&
    cleaned.category &&
    cleaned.date.startsWith(currentMonthKey())
  ) {
    const category = categoriesById.value.get(cleaned.category)
    if (category?.budget && category.budget > 0) {
      const spendMap = currentMonthSpendByCategory.value
      const totalSpent = spendMap.get(category.id) ?? spendMap.get(category.name) ?? 0

      // Trigger budget alert check (async, non-blocking)
      checkBudgetAlert(category, totalSpent).catch(() => {
        // Ignore notification errors
      })
    }
  }

  // If this is a recurring expense template, refresh bill reminders
  if (cleaned.isRecurring) {
    refreshAllNotifications().catch(() => {
      // Ignore notification errors
    })
  }

  return id
}

// Every mutator below applies its change optimistically (see the optimistic
// helpers above) so the UI reflects writes immediately; the Realtime refetch
// reconciles with the server's authoritative state.
export function updateExpense(expenseId: string, updates: DocUpdate<FirestoreExpense>) {
  return optimisticUpdate<FirestoreExpense>(
    () => state.expenses,
    (rows) => {
      state.expenses = rows
    },
    expenseId,
    updates,
    () => databaseService.updateExpense(expenseId, updates),
  )
}

export function deleteExpense(expenseId: string) {
  return optimisticDelete(
    () => state.expenses,
    (rows) => {
      state.expenses = rows
    },
    expenseId,
    () => databaseService.deleteExpense(expenseId),
  )
}

// SETTLEMENT OPERATIONS (household repayments)
export function addSettlement(settlement: Omit<FirestoreSettlement, "id" | "userId" | "createdAt" | "updatedAt">) {
  return optimisticCreate<FirestoreSettlement>(
    () => state.settlements,
    (rows) => {
      state.settlements = rows
    },
    settlement,
    () => databaseService.createSettlement(settlement),
    byDateDesc,
  )
}

export function deleteSettlement(settlementId: string) {
  return optimisticDelete(
    () => state.settlements,
    (rows) => {
      state.settlements = rows
    },
    settlementId,
    () => databaseService.deleteSettlement(settlementId),
  )
}

// CATEGORY OPERATIONS
export function addCategory(category: Omit<FirestoreCategory, "id" | "userId" | "createdAt" | "updatedAt">) {
  const cleaned = { ...category, name: sanitizeText(category.name || "") }
  return optimisticCreate<FirestoreCategory>(
    () => state.categories,
    (rows) => {
      state.categories = rows
    },
    cleaned,
    () => databaseService.createCategory(cleaned),
    byName,
  )
}

export function updateCategory(
  categoryId: string,
  updates: Partial<Omit<FirestoreCategory, "id" | "userId" | "createdAt">>,
) {
  return optimisticUpdate<FirestoreCategory>(
    () => state.categories,
    (rows) => {
      state.categories = rows
    },
    categoryId,
    updates,
    () => databaseService.updateCategory(categoryId, updates),
  )
}

export function deleteCategory(categoryId: string) {
  return optimisticDelete(
    () => state.categories,
    (rows) => {
      state.categories = rows
    },
    categoryId,
    () => databaseService.deleteCategory(categoryId),
  )
}

// ACCOUNT OPERATIONS
export function addAccount(account: Omit<FirestoreAccount, "id" | "userId" | "createdAt" | "updatedAt">) {
  const cleaned = { ...account, name: sanitizeText(account.name || "") }
  return optimisticCreate<FirestoreAccount>(
    () => state.accounts,
    (rows) => {
      state.accounts = rows
    },
    cleaned,
    () => databaseService.createAccount(cleaned),
    byName,
  )
}

export function updateAccount(
  accountId: string,
  updates: Partial<Omit<FirestoreAccount, "id" | "userId" | "createdAt">>,
) {
  return optimisticUpdate<FirestoreAccount>(
    () => state.accounts,
    (rows) => {
      state.accounts = rows
    },
    accountId,
    updates,
    () => databaseService.updateAccount(accountId, updates),
  )
}

export async function deleteAccount(accountId: string) {
  // Expenses reference an account by accountId (or, on legacy rows, by
  // id-as-text / name in the account string), savings by id — check all forms
  // so the guard actually catches in-use accounts.
  const account = state.accounts.find((a) => a.id === accountId)
  const isInUse =
    state.expenses.some(
      (expense) =>
        expense.accountId === accountId ||
        expense.account === accountId ||
        (!!account && expense.account === account.name),
    ) || state.savingsGoals.some((goal) => goal.accountId === accountId)

  if (isInUse) {
    throw new Error("Cannot delete account: it is used by existing expenses or savings")
  }
  await optimisticDelete(
    () => state.accounts,
    (rows) => {
      state.accounts = rows
    },
    accountId,
    () => databaseService.deleteAccount(accountId),
  )
}

// INCOME OPERATIONS
export function addIncome(income: Omit<FirestoreIncome, "id" | "userId" | "createdAt" | "updatedAt">) {
  const cleaned = { ...income, source: sanitizeText(income.source || "") }
  return optimisticCreate<FirestoreIncome>(
    () => state.income,
    (rows) => {
      state.income = rows
    },
    cleaned,
    () => databaseService.createIncome(cleaned),
    (a, b) => a.source.localeCompare(b.source),
  )
}

export function updateIncome(incomeId: string, updates: Partial<Omit<FirestoreIncome, "id" | "userId" | "createdAt">>) {
  return optimisticUpdate<FirestoreIncome>(
    () => state.income,
    (rows) => {
      state.income = rows
    },
    incomeId,
    updates,
    () => databaseService.updateIncome(incomeId, updates),
  )
}

export function deleteIncome(incomeId: string) {
  return optimisticDelete(
    () => state.income,
    (rows) => {
      state.income = rows
    },
    incomeId,
    () => databaseService.deleteIncome(incomeId),
  )
}

// SAVINGS OPERATIONS
export function addSavingsGoal(goal: Omit<FirestoreSavingsGoal, "id" | "userId" | "createdAt" | "updatedAt">) {
  return optimisticCreate<FirestoreSavingsGoal>(
    () => state.savingsGoals,
    (rows) => {
      state.savingsGoals = rows
    },
    goal,
    () => databaseService.createSavingsGoal(goal),
  )
}

export async function updateSavingsGoal(
  goalId: string,
  updates: Partial<Omit<FirestoreSavingsGoal, "id" | "userId" | "createdAt">>,
) {
  // Get the goal before updating to check for milestone notifications
  const goal = state.savingsGoals.find((g) => g.id === goalId)

  await optimisticUpdate<FirestoreSavingsGoal>(
    () => state.savingsGoals,
    (rows) => {
      state.savingsGoals = rows
    },
    goalId,
    updates,
    () => databaseService.updateSavingsGoal(goalId, updates),
  )

  // Check for milestone notifications if currentAmount was updated
  if (goal && updates.currentAmount !== undefined) {
    const newAmount = updates.currentAmount
    checkGoalMilestone(goal.name, newAmount, goal.targetAmount).catch(() => {
      // Ignore notification errors
    })
  }
}

export function deleteSavingsGoal(goalId: string) {
  return optimisticDelete(
    () => state.savingsGoals,
    (rows) => {
      state.savingsGoals = rows
    },
    goalId,
    () => databaseService.deleteSavingsGoal(goalId),
  )
}

// USER PREFERENCES
export async function updateUserPreferences(
  preferences: Partial<Omit<FirestoreUserPreferences, "id" | "userId" | "createdAt">>,
) {
  // Optimistic, like the collection mutators above: apply locally right away;
  // keep the change if the write got queued in the offline outbox, roll back
  // and rethrow on a real HTTP failure.
  const previous = state.preferences
  const previousCurrency = personalCurrency.value
  state.preferences = { ...(previous ?? {}), ...preferences } as FirestoreUserPreferences
  if (preferences.currency !== undefined) {
    personalCurrency.value = preferences.currency ?? null
  }

  try {
    await databaseService.updateUserPreferences(preferences)
  } catch (error) {
    if (isQueuedOfflineError(error)) return
    state.preferences = previous
    personalCurrency.value = previousCurrency
    throw error
  }

  // Reload the authoritative copy. Non-fatal: the update itself succeeded, so
  // keep the optimistic state if the refetch fails.
  try {
    state.preferences = await databaseService.getUserPreferences()
    personalCurrency.value = state.preferences?.currency ?? null
  } catch (error) {
    logger.warn("Failed to reload preferences after update", { error })
  }
}

// INVESTMENT OPERATIONS
export function addInvestment(investment: Omit<FirestoreInvestment, "id" | "userId" | "createdAt" | "updatedAt">) {
  return optimisticCreate<FirestoreInvestment>(
    () => state.investments,
    (rows) => {
      state.investments = rows
    },
    investment,
    () => databaseService.createInvestment(investment),
    byName,
  )
}

export function updateInvestment(
  investmentId: string,
  updates: Partial<Omit<FirestoreInvestment, "id" | "userId" | "createdAt">>,
) {
  return optimisticUpdate<FirestoreInvestment>(
    () => state.investments,
    (rows) => {
      state.investments = rows
    },
    investmentId,
    updates,
    () => databaseService.updateInvestment(investmentId, updates),
  )
}

export function deleteInvestment(investmentId: string) {
  return optimisticDelete(
    () => state.investments,
    (rows) => {
      state.investments = rows
    },
    investmentId,
    () => databaseService.deleteInvestment(investmentId),
  )
}

// DEBT OPERATIONS
export function addDebt(debt: Omit<FirestoreDebt, "id" | "userId" | "createdAt" | "updatedAt">) {
  return optimisticCreate<FirestoreDebt>(
    () => state.debts,
    (rows) => {
      state.debts = rows
    },
    debt,
    () => databaseService.createDebt(debt),
    byName,
  )
}

export function updateDebt(debtId: string, updates: Partial<Omit<FirestoreDebt, "id" | "userId" | "createdAt">>) {
  return optimisticUpdate<FirestoreDebt>(
    () => state.debts,
    (rows) => {
      state.debts = rows
    },
    debtId,
    updates,
    () => databaseService.updateDebt(debtId, updates),
  )
}

export function deleteDebt(debtId: string) {
  return optimisticDelete(
    () => state.debts,
    (rows) => {
      state.debts = rows
    },
    debtId,
    () => databaseService.deleteDebt(debtId),
  )
}

// SAVINGS TRANSACTION OPERATIONS
export function addSavingsTransaction(
  transaction: Omit<FirestoreSavingsTransaction, "id" | "userId" | "createdAt" | "updatedAt">,
) {
  return optimisticCreate<FirestoreSavingsTransaction>(
    () => state.savingsTransactions,
    (rows) => {
      state.savingsTransactions = rows
    },
    transaction,
    () => databaseService.createSavingsTransaction(transaction),
    byDateDesc,
  )
}

export function updateSavingsTransaction(
  transactionId: string,
  updates: Partial<Omit<FirestoreSavingsTransaction, "id" | "userId" | "createdAt">>,
) {
  return optimisticUpdate<FirestoreSavingsTransaction>(
    () => state.savingsTransactions,
    (rows) => {
      state.savingsTransactions = rows
    },
    transactionId,
    updates,
    () => databaseService.updateSavingsTransaction(transactionId, updates),
  )
}

export function deleteSavingsTransaction(transactionId: string) {
  return optimisticDelete(
    () => state.savingsTransactions,
    (rows) => {
      state.savingsTransactions = rows
    },
    transactionId,
    () => databaseService.deleteSavingsTransaction(transactionId),
  )
}

// EXPENSE COMMENT OPERATIONS (note thread on a transaction)
export function addExpenseComment(comment: Omit<FirestoreExpenseComment, "id" | "userId" | "createdAt" | "updatedAt">) {
  const cleaned = { ...comment, text: sanitizeText(comment.text || "") }
  return optimisticCreate<FirestoreExpenseComment>(
    () => state.expenseComments,
    (rows) => {
      state.expenseComments = rows
    },
    cleaned,
    () => databaseService.createExpenseComment(cleaned),
  )
}

export function deleteExpenseComment(commentId: string) {
  return optimisticDelete(
    () => state.expenseComments,
    (rows) => {
      state.expenseComments = rows
    },
    commentId,
    () => databaseService.deleteExpenseComment(commentId),
  )
}

// Export the reactive state for components to use
export const firestoreState = {
  expenses: computed(() => state.expenses),
  categories: computed(() => state.categories),
  accounts: computed(() => state.accounts),
  income: computed(() => state.income),
  savingsGoals: computed(() => state.savingsGoals),
  investments: computed(() => state.investments),
  debts: computed(() => state.debts),
  savingsTransactions: computed(() => state.savingsTransactions),
  settlements: computed(() => state.settlements),
  expenseComments: computed(() => state.expenseComments),
  preferences: computed(() => state.preferences),
  isLoading: computed(() => state.isLoading),
  isInitialized: computed(() => state.isInitialized),
  error: computed(() => state.error),
  categoriesById,
  accountsById,
}
